import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { OverlayType } from '@shared/models/googlemaps.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import {
  selectIsSingleCounty,
  selectMapToolsHasAccess,
  selectPreferenceElement,
  selectPreferenceValue,
  selectTemplate
} from '@store/user/user.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PG_MAP_TOOLS_KEYS, PreferenceCode, PreferenceElement, PreferenceGroup, Template, TemplateCode } from '@store/user/user.model';
import { SidePanelTab, SidePanelView } from '@store/search-board/search-board.model';
import { combineLatest, Observable } from 'rxjs';
import {map, take, withLatestFrom} from 'rxjs/operators';
import {selectSelectedSearch, selectSidePanelState} from '@store/search-board/search-board.selector';
import {GetMySearchData} from "@store/user/user.actions";

@UntilDestroy()
@Component({
  selector: 'rlst-search-map-menu',
  templateUrl: './search-map-menu.component.html',
  styleUrls: ['./search-map-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class SearchMapMenuComponent implements OnInit {
  @Input() isPremium: boolean = false;
  @Input() selectedDrawingTool: OverlayType;
  @Input() hasClearAll: boolean = false;
  @Output() useDrawingTool: EventEmitter<OverlayType> = new EventEmitter();
  @Output() useAnnotationTool: EventEmitter<void> = new EventEmitter();
  @Output() locateOnMapTool: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() clearSelectedLayers: EventEmitter<void> = new EventEmitter();
  private preferenceGroupList: any;
  public propertySalesMenuOpenIndex: number = 0;
  public premiumSearchFieldsMenuOpenIndex: number = 0;
  public trendMenuOpenIndex: number = 0;
  public isPremiumLayerWorks$: Observable<boolean>;
  public isSingleCounty$: Observable<boolean>;

  public readonly drawingTools: typeof OverlayType = OverlayType;
  public readonly mapToolsKeys = PG_MAP_TOOLS_KEYS;
  public sidePanelTabOpened: SidePanelTab;
  public isMySearch: boolean;
  public isSelectedSearch$: Observable<boolean>;


  constructor(
    private readonly store$: Store<AppState>,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  public ngOnInit(): void {
    this.store$.pipe(
      select(selectMapToolsHasAccess),
      untilDestroyed(this)
    ).subscribe((data) => this.preferenceGroupList = data);

    this.isPremiumLayerWorks$ = this.store$
      .pipe(
        select(selectPreferenceValue(PreferenceCode.PG_RL_PREMIUM_SEARCH, PreferenceCode.PREF_RL_PREMIUM_SEARCH)),
        withLatestFrom(
          this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TOOLS,
            PreferenceCode.PREF_PREMIUM_MAP_LAYERS), untilDestroyed(this)))
        ),
        map(([searchValue, mapValue]) => {
          return searchValue !== 'N' && mapValue.hasAccess;
        }),
        untilDestroyed(this)
      );
    this.isSingleCounty$ = this.store$.pipe(select(selectIsSingleCounty), untilDestroyed(this));
    this.store$
      .pipe(
        select(selectSidePanelState),
        untilDestroyed(this)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.isMySearch = sidePanelView.sidePanelTabOpened === SidePanelTab.mySearch;
        this.cdr.markForCheck();
      });
    this.getPremiumTemplate();

    this.isSelectedSearch$ = this.store$.pipe(select(selectSelectedSearch), map((value) => {
      return !!value;
    }));
  }

  public getGroup(code: string): PreferenceGroup {
    if (this.preferenceGroupList) {
      return this.preferenceGroupList
        .find((group: Partial<PreferenceGroup> & Partial<PreferenceElement>) =>
          (group.prefGroupCode === 'PG_MAP_TOOLS' ? group.prefCode : group.prefGroupCode) === code);
    }
  }

  public onMenuOpened(layerClicked: string) {
    if (layerClicked === this.mapToolsKeys.PG_MAP_LAYERS) {
      this.propertySalesMenuOpenIndex++;
    }
    if (layerClicked === this.mapToolsKeys.PG_MAP_TRENDS_LAYER) {
      this.trendMenuOpenIndex++;
    }
    if (layerClicked === this.mapToolsKeys.PREMIUM_MAP_SEARCH_FIELDS) {
      this.premiumSearchFieldsMenuOpenIndex++;
    }
  }

  public openDrawingTool(drawingToolType: OverlayType): void {
      this.isSelectedSearch$.pipe(take(1)).subscribe((value) => {
        if (!value) {
          this.store$.dispatch(new GetMySearchData());
        }
      })

    this.useDrawingTool.emit(drawingToolType);
  }


  private getPremiumTemplate(): void {
    combineLatest(
      [
        this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_PREMIUM_SEARCH), untilDestroyed(this)))
      ]
    ).pipe(
      map(([premiumTemplate]: [Template]) => premiumTemplate)).subscribe();
  }
}
